import React, { FC, ReactElement, useState } from "react";
import { useDebounce } from "use-debounce";

import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  ListItem,
  ListItemText,
} from "@mui/material";

import { useSelector } from "../store";
import { useListRoutesQuery } from "../store/enhancedApi";
import { EntityInfo } from "../store/pomeriumApi";
import TextField from "./styled/TextField";

type RouteSelectorProps = {
  routeId: string | null;
  onRouteIdChange: (routeId: string | null) => void;
} & Omit<
  AutocompleteProps<EntityInfo, false, false, false, "div">,
  "renderInput" | "options"
>;

const RouteSelector: FC<RouteSelectorProps> = ({
  routeId,
  onRouteIdChange,
  ...props
}) => {
  const organizationId = useSelector((state) => state.current.organizationId);
  const clusterId = useSelector((state) => state.current.clusterId);
  const [inputValue, setInputValue] = useState("");
  const [delayedInputValue, { isPending }] = useDebounce(inputValue, 250);
  const [open, setOpen] = useState(false);

  const { data: result, isFetching } = useListRoutesQuery(
    {
      organizationId,
      namespaceId: clusterId,
      includeDescendants: true,
      limit: 10,
      filter: JSON.stringify({
        items: [
          { field: "name", operator: "contains", value: delayedInputValue },
        ],
      }),
    },
    { skip: !organizationId || !clusterId },
  );
  const isLoading = isFetching || isPending();
  let routes: EntityInfo[] = [];
  if (!open || !isLoading) {
    routes =
      result?.rows.map((r) => ({
        id: r.id,
        name: r.name,
        updatedAt: r.updatedAt,
      })) || [];
    routes.push({ id: "", name: "All Routes", updatedAt: "" });
  }
  const selectedRoute = open
    ? null
    : routes.find((r) => r.id === routeId) || null;

  const onChange = (
    _evt: React.SyntheticEvent,
    route: EntityInfo | null,
  ): void => {
    onRouteIdChange(route?.id || null);
  };
  const onInputChange = (
    _evt: React.SyntheticEvent,
    newInputValue: string,
  ): void => {
    setInputValue(newInputValue);
  };
  const onOpen = (): void => {
    setOpen(true);
  };
  const onClose = (): void => {
    setOpen(false);
  };

  return (
    <Autocomplete<EntityInfo, false, false, false, "div">
      {...props}
      fullWidth
      size="small"
      loading={isLoading}
      value={selectedRoute}
      options={routes}
      filterOptions={(x) => x}
      getOptionLabel={(option: EntityInfo) => option.name}
      isOptionEqualToValue={(x: EntityInfo, y: EntityInfo): boolean =>
        x?.id === y?.id
      }
      renderInput={(params: AutocompleteRenderInputParams): ReactElement => (
        <TextField {...params} />
      )}
      renderOption={(props, route: EntityInfo): ReactElement => {
        const { key, ...optionProps } = props;
        return (
          <ListItem key={key} {...optionProps}>
            <ListItemText primary={route.name} />
          </ListItem>
        );
      }}
      open={open}
      onChange={onChange}
      onInputChange={onInputChange}
      onOpen={onOpen}
      onClose={onClose}
      blurOnSelect
    />
  );
};
export default RouteSelector;
